import { isCredentialRejected } from './helpers/credential-rejected.helpers';
import { isExpiring } from './helpers/expiry.helpers';
import { singleFlight, withTimeout } from './helpers/single-flight.helpers';
import { createBrowserClock, createNoopLogger } from './ports/browser-ports';
import { ClockPort, LoggerPort } from './ports/ports.types';
import {
  CredentialSource,
  SignInOptions,
  SignOutOptions,
} from './sources/credential-source.types';
import {
  AuthErrorCode,
  AuthStatus,
  DEFAULT_RENEW_RETRY_DELAYS_MS,
  DEFAULT_RENEW_SKEW_SECONDS,
  SessionEndReason,
} from './auth-core.const';
import {
  AuthError,
  AuthSnapshot,
  Credential,
  IdentityFence,
  Unsubscribe,
} from './auth-core.types';
import { AuthStore } from './auth-store';
import { RenewalScheduler } from './renewal-scheduler';

export type AuthClientOptions = {
  source: CredentialSource;
  clock?: ClockPort;
  logger?: LoggerPort;
  /** Seconds before `exp` at which a credential counts as due for renewal. */
  renewSkewSeconds?: number;
  identityFence?: IdentityFence;
  /** Budget for the identity fence. Exceeding it fails open with a warning. */
  identityFenceTimeoutMs?: number;
  /**
   * Budget for a single renewal. Without one, a renewal that never settles is
   * held by the single-flight guard and handed to every later caller, so one
   * hung request silently disables renewal for the rest of the page's life.
   */
  renewTimeoutMs?: number;
  /** How often to check whether the credential is due for renewal. */
  renewCheckIntervalMs?: number;
  /** Delays before each retry after a failed renewal. */
  renewRetryDelaysMs?: readonly number[];
  /**
   * Clears whatever the *app* persists beyond the credential — query cache,
   * persisted stores, session keys. The core cannot know these, but a teardown
   * that leaves one user's company id in place is how the cross-app identity
   * bug survives a logout.
   */
  onReset?: () => void | Promise<void>;
};

const DEFAULT_FENCE_TIMEOUT_MS = 5_000;

// Comfortably beyond oidc-client-ts's own 10s silent-request timeout, so this
// only fires when that mechanism has itself failed to settle.
const DEFAULT_RENEW_TIMEOUT_MS = 30_000;

export type ClientSignInOptions = SignInOptions & {
  /** Try a non-interactive sign-in before falling back to a redirect. */
  silentFirst?: boolean;
};

export type ClientSignOutOptions = SignOutOptions & {
  /**
   * Why the session is ending, surfaced on the snapshot so the logout screen
   * can say something true. Defaults to an ordinary sign-out; an inactivity
   * policy should pass {@link SessionEndReason.Inactivity}.
   */
  reason?: SessionEndReason;
};

export type ResetOptions = {
  reason?: SessionEndReason;
};

/**
 * The single stateful object the rest of the world talks to.
 *
 * Created once, outside any framework, before the UI mounts. That placement is
 * deliberate and load-bearing:
 *
 * - HTTP interceptors and SignalR can read a token **synchronously**, instead
 *   of the current arrangement where a second token store exists purely because
 *   auth is only reachable through a React hook;
 * - an embedding host's token can be adopted before first paint;
 * - there is exactly one instance, so there is exactly one renewal loop.
 */
export class AuthClient {
  private readonly store = new AuthStore();
  private readonly source: CredentialSource;
  private readonly clock: ClockPort;
  private readonly logger: LoggerPort;
  private readonly renewSkewSeconds: number;
  private readonly identityFence: IdentityFence | undefined;
  private readonly identityFenceTimeoutMs: number;
  private readonly onReset: (() => void | Promise<void>) | undefined;
  private readonly renewOnce: () => Promise<Credential | null>;
  private readonly renewRetryDelaysMs: readonly number[];
  private readonly scheduler: RenewalScheduler;

  private unsubscribeSource: Unsubscribe | null = null;
  private startPromise: Promise<AuthSnapshot> | null = null;
  private endingSession: Promise<void> | null = null;
  private disposed = false;

  constructor(options: AuthClientOptions) {
    this.source = options.source;
    this.clock = options.clock ?? createBrowserClock();
    this.logger = options.logger ?? createNoopLogger();
    this.renewSkewSeconds = options.renewSkewSeconds ?? DEFAULT_RENEW_SKEW_SECONDS;
    this.identityFence = options.identityFence;
    this.identityFenceTimeoutMs =
      options.identityFenceTimeoutMs ?? DEFAULT_FENCE_TIMEOUT_MS;
    this.onReset = options.onReset;
    this.renewRetryDelaysMs = options.renewRetryDelaysMs ?? DEFAULT_RENEW_RETRY_DELAYS_MS;
    this.renewOnce = singleFlight(() =>
      withTimeout(
        this.source.renew(),
        options.renewTimeoutMs ?? DEFAULT_RENEW_TIMEOUT_MS,
        () => new Error('Credential renewal timed out'),
      ),
    );
    this.scheduler = new RenewalScheduler({
      isDue: () => this.isRenewalDue(),
      renew: () => this.renewOrThrow(),
      onExhausted: (error) => this.reportRenewalFailure(error),
      clock: this.clock,
      logger: this.logger,
      intervalMs: options.renewCheckIntervalMs,
      retryDelaysMs: this.renewRetryDelaysMs,
      isTerminal: isCredentialRejected,
    });
  }

  // ---------------------------------------------------------------- lifecycle

  /**
   * Runs the ordered boot sequence: resolve a credential, then run the identity
   * fence, then report ready. Idempotent — concurrent callers share one run.
   *
   * Consumers must await this before adopting persona or company state, which
   * is what gives CORE-2655 §4.3's "the fence must run before adoption" an
   * enforceable meaning rather than a convention.
   */
  start(): Promise<AuthSnapshot> {
    this.startPromise ??= this.runStartSequence();
    return this.startPromise;
  }

  private async runStartSequence(): Promise<AuthSnapshot> {
    this.store.patch({ status: AuthStatus.Starting, isLoading: true });
    this.unsubscribeSource = this.source.subscribe((credential, reason) =>
      this.handleSourceChange(credential, reason),
    );

    try {
      const credential = await this.source.init();
      const fenced = await this.applyIdentityFence(credential);
      this.applyCredential(fenced);
    } catch (error) {
      this.fail({
        code: AuthErrorCode.SourceError,
        message: 'Failed to resolve a credential',
        cause: error,
      });
    }

    // Started unconditionally, and only after the boot sequence, so renewal is
    // never racing credential resolution. It runs regardless of user activity —
    // an inactivity policy belongs to the app and must end the session itself
    // rather than by withholding renewal.
    this.scheduler.start();

    return this.store.getSnapshot();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.scheduler.stop();
    this.unsubscribeSource?.();
    this.unsubscribeSource = null;
    this.source.dispose();
    this.store.clear();
  }

  // ------------------------------------------------------------------- reads

  getSnapshot(): AuthSnapshot {
    return this.store.getSnapshot();
  }

  subscribe(listener: (snapshot: AuthSnapshot) => void): Unsubscribe {
    return this.store.subscribe(listener);
  }

  /**
   * The current access token, or null. **Synchronous and never renews** — safe
   * to call from an axios request interceptor or a SignalR token factory.
   */
  getToken(): string | null {
    return this.source.getCredential()?.accessToken ?? null;
  }

  /**
   * The current identity claims, or null.
   *
   * Kept off {@link AuthSnapshot} so the snapshot stays small and safe to log,
   * but reachable because consumers legitimately need them — Hire puts the
   * whole profile into redux, and callers here read `sub`.
   */
  getClaims(): Readonly<Record<string, unknown>> | null {
    return this.source.getCredential()?.claims ?? null;
  }

  /**
   * The raw id token, or null.
   *
   * Needed because `oidc-client-ts` strips `auth_time` from the parsed profile,
   * and the cross-app logout-staleness check has no other way to read it. Both
   * xemplo-fe and hr-fe currently hand-decode this token to get at that claim.
   */
  getIdToken(): string | null {
    return this.source.getCredential()?.idToken ?? null;
  }

  /** True when the credential is absent or within the renewal skew of expiry. */
  isExpiring(): boolean {
    const credential = this.source.getCredential();
    if (!credential) return true;
    return isExpiring(credential.expiresAt, this.clock.now(), this.renewSkewSeconds);
  }

  /**
   * Whether the scheduler should renew now.
   *
   * Distinct from {@link isExpiring}, which reports true with no credential at
   * all — correct for "is this token usable", wrong for scheduling. Renewing
   * when signed out would attempt one on every tick of a logged-out page.
   */
  private isRenewalDue(): boolean {
    const credential = this.source.getCredential();
    if (!credential) return false;
    return isExpiring(credential.expiresAt, this.clock.now(), this.renewSkewSeconds);
  }

  // ------------------------------------------------------------------ writes

  /**
   * A token guaranteed fresh at the moment it is returned, renewing first if
   * needed. Use on the 401-recovery path; use {@link getToken} on the hot path.
   */
  async getFreshToken(): Promise<string | null> {
    if (!this.isExpiring()) return this.getToken();

    const credential = await this.renew();
    return credential?.accessToken ?? null;
  }

  /** Renews. Concurrent callers collapse into one request to the source. */
  async renew(): Promise<Credential | null> {
    try {
      return await this.renewOrThrow();
    } catch (error) {
      this.reportRenewalFailure(error);
      return null;
    }
  }

  /**
   * The single renewal path. Everything that renews goes through here, so one
   * refresh token is never spent twice concurrently.
   */
  private async renewOrThrow(): Promise<Credential | null> {
    const credential = await this.renewOnce();
    this.applyCredential(credential);
    return credential;
  }

  /**
   * Signs in, optionally probing for an existing identity-provider session
   * first so a user with SSO never sees a redirect.
   *
   * Both consumers want silent-first: Hire's `signIn` is a silent→redirect
   * cascade, and xemplo-fe's `use-login` tries silent before redirecting.
   */
  async signIn(options: ClientSignInOptions = {}): Promise<void> {
    const { silentFirst, ...signInOptions } = options;
    if (silentFirst && (await this.trySilentSignIn())) return;

    if (!this.source.signIn) throw notSupported('signIn', this.source.kind);
    await this.source.signIn(signInOptions);
  }

  /**
   * Probes for an existing identity-provider session without redirecting.
   * Returns whether one was adopted.
   *
   * Distinct from {@link renew} in its failure semantics: having no session to
   * adopt is an ordinary outcome, not an error, so it must not put the client
   * into a failed state the way a failed renewal does.
   */
  async trySilentSignIn(): Promise<boolean> {
    try {
      const credential = await this.renewOnce();
      this.applyCredential(credential);
      return Boolean(credential);
    } catch (error) {
      this.logger.debug('No silent session available', { error });
      return false;
    }
  }

  async signOut(options: ClientSignOutOptions = {}): Promise<void> {
    const { reason = SessionEndReason.SignedOut, ...signOutOptions } = options;

    await this.reset({ reason });
    if (!this.source.signOut) return;
    await this.source.signOut(signOutOptions);
  }

  /**
   * Tears the local session down: drops the credential and runs the app's own
   * teardown. Does not end the remote session — {@link signOut} does that.
   */
  async reset(options: ResetOptions = {}): Promise<void> {
    await this.endSession(options.reason ?? SessionEndReason.SignedOut);
  }

  // ----------------------------------------------------------------- internal

  /**
   * The one way a session ends, whatever ended it: the credential is dropped
   * locally, the app's own state is cleared, and the snapshot says why.
   * Concurrent callers share one run, so the teardown and its log happen once.
   */
  private endSession(reason: SessionEndReason, error?: AuthError): Promise<void> {
    this.endingSession ??= this.runEndSession(reason, error).finally(() => {
      this.endingSession = null;
    });
    return this.endingSession;
  }

  private async runEndSession(
    reason: SessionEndReason,
    error?: AuthError,
  ): Promise<void> {
    if (error) {
      this.logger.error(error.message, { code: error.code, reason, error: error.cause });
    }
    await this.source.revoke();
    await this.onReset?.();
    this.applyCredential(null, reason, error);
  }

  /**
   * The source reports a credential change it made on its own. An expiry is
   * renewed before the session is ended: after a suspended laptop wakes, the
   * provider's expiry timer can fire before the scheduler's tick, and the
   * refresh token is usually still good.
   */
  private handleSourceChange(
    credential: Credential | null,
    reason?: SessionEndReason,
  ): void {
    if (credential) return this.applyCredential(credential);
    // Our own teardown revokes the source, which reports back here.
    if (this.endingSession) return;
    if (reason === SessionEndReason.Expired)
      return this.inBackground(this.recoverExpiredSession());
    if (reason) return this.inBackground(this.endSession(reason));
    this.applyCredential(null);
  }

  /** For work started from a synchronous callback, which cannot await it. */
  private inBackground(task: Promise<void>): void {
    task.catch((error: unknown) => {
      this.logger.error('Session teardown failed', { error });
    });
  }

  /**
   * Retries on the scheduler's backoff, because a laptop that has just woken
   * often has no network yet, and one failed request must not sign it out.
   */
  private async recoverExpiredSession(): Promise<void> {
    let lastError: unknown;

    for (const delayMs of [0, ...this.renewRetryDelaysMs]) {
      if (delayMs) await wait(delayMs);
      // Recovered by another route meanwhile — the 401 path, or a sign-in.
      if (this.disposed || this.source.getCredential()) return;

      try {
        const credential = await this.renewOnce();
        if (!credential) break;
        return this.applyCredential(credential);
      } catch (error) {
        lastError = error;
        if (isCredentialRejected(error)) break;
      }
    }

    await this.endSession(SessionEndReason.Expired, {
      code: AuthErrorCode.RenewFailed,
      message: 'The credential expired and could not be renewed',
      cause: lastError,
    });
  }

  /**
   * CORE-2655 §4.3 Layer 1. Fails **open** on an unknown expected subject and
   * on its own timeout — an unreachable check must not lock everyone out — but
   * fails **closed** on a genuine mismatch.
   */
  private async applyIdentityFence(
    credential: Credential | null,
  ): Promise<Credential | null> {
    if (!this.identityFence || !credential?.subject) return credential;

    const expected = await this.resolveExpectedSubject();
    if (!expected || expected === credential.subject) return credential;

    this.logger.warn('Identity fence rejected the local session', {
      code: AuthErrorCode.IdentityMismatch,
    });
    await this.identityFence.onMismatch?.({
      expected,
      actual: credential.subject,
    });
    await this.reset({ reason: SessionEndReason.IdentityMismatch });

    this.store.patch({
      error: {
        code: AuthErrorCode.IdentityMismatch,
        message: 'The stored session belongs to a different user',
      },
    });
    return null;
  }

  private async resolveExpectedSubject(): Promise<string | null> {
    try {
      return await withTimeout(
        Promise.resolve(this.identityFence?.resolveExpectedSubject() ?? null),
        this.identityFenceTimeoutMs,
        () => new Error('Identity fence timed out'),
      );
    } catch (error) {
      this.logger.warn('Identity fence unavailable; continuing', { error });
      return null;
    }
  }

  private applyCredential(
    credential: Credential | null,
    reason?: SessionEndReason,
    error?: AuthError,
  ): void {
    // A credential arriving by *any* route — a retry, the 401 path, a host push
    // — clears the failure state, so scheduling resumes after exhaustion.
    if (credential) this.scheduler.reset();

    const previous = this.store.getSnapshot();

    this.store.patch({
      status: credential ? AuthStatus.Authenticated : AuthStatus.Unauthenticated,
      isAuthenticated: Boolean(credential),
      isLoading: false,
      subject: credential?.subject ?? null,
      expiresAt: credential?.expiresAt ?? null,
      sourceKind: this.source.kind,
      error: credential ? null : (error ?? previous.error),
      endReason: credential ? null : (reason ?? previous.endReason),
    });
  }

  /**
   * Reports a failed renewal at the severity the situation warrants.
   *
   * While the credential is still valid this is a **warning, not a sign-out**:
   * renewal has 120 seconds of runway, and tearing the session down at the first
   * failure is what loses a half-finished performance review. The app gets an
   * `error` on the snapshot with the session still live, and the credential is
   * dropped later by `accessTokenExpired` if renewal never succeeds.
   */
  private reportRenewalFailure(error: unknown): void {
    const stillUsable = Boolean(this.source.getCredential());
    const authError: AuthError = {
      code: AuthErrorCode.RenewFailed,
      message: 'Could not renew the credential',
      cause: error,
    };

    // The authority refused the chain, so the runway left on the access token
    // buys nothing: no later renewal can succeed.
    if (isCredentialRejected(error)) {
      this.endRejectedSession(authError);
      return;
    }

    this.logger.error(authError.message, {
      code: authError.code,
      stillUsable,
      error,
    });

    if (stillUsable) {
      this.store.patch({ error: authError });
      return;
    }

    this.store.patch({
      status: AuthStatus.Failed,
      isAuthenticated: false,
      isLoading: false,
      error: authError,
      endReason: SessionEndReason.RenewFailed,
    });
  }

  /**
   * A 401 still in flight from the ended session renews with no refresh token,
   * which is refused. Once the session has ended there is nothing left to tear
   * down, and running it again would relabel a sign-out as a renewal failure.
   */
  private endRejectedSession(authError: AuthError): void {
    if (this.store.getSnapshot().status === AuthStatus.Unauthenticated) {
      this.logger.debug('Renewal refused with no session to end');
      return;
    }
    this.inBackground(this.endSession(SessionEndReason.RenewFailed, authError));
  }

  private fail(error: AuthError): void {
    // `cause` is passed through unwrapped rather than stringified: a telemetry
    // adapter wants the Error to capture a stack from, and `String(error)`
    // throws away the only part worth having.
    this.logger.error(error.message, { code: error.code, error: error.cause });
    this.store.patch({
      status: AuthStatus.Failed,
      isAuthenticated: false,
      isLoading: false,
      error,
    });
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function notSupported(operation: string, kind: string): Error {
  return Object.assign(new Error(`${operation} is not supported by the ${kind} source`), {
    code: AuthErrorCode.NotSupported,
  });
}
